<script setup lang="ts">
  import { z } from 'zod';
  import { JobApplicationFooBar } from '@borg/types';
  import {
    MpAccordion,
    MpButton,
    MpCard,
    MpCheckbox,
    MpDivider,
    MpField,
    MpIcon,
    MpInput,
    MpSelect,
    MpText,
    MpTextArea,
    openMpConfirmModal,
    pushToastMessage,
    useMpScreen,
  } from '@borg/ui';
  import { isEmptyArray, isEmptyString, isNull } from '@borg/utils';
  import { getNationalitiesForLocale, NationalityOption } from '@borg/core/utils/nationalities';
  import PreviousCoverLettersList from '../PreviousCoverLettersList.vue';

  const emit = defineEmits<{
    (event: 'next'): void;
  }>();

  defineProps<{
    disabled?: boolean;
  }>();

  const { t, locale } = useI18n();
  const route = useRoute();
  const projectConfig = useProjectConfig();
  const { isMobile } = useMpScreen();
  const userStore = useUserStore();
  const jobId = route.params.jobId?.toString();
  const application = jobApplicationRepository.use();
  const form = applyForm.inject();
  const isCvRequired = application.value?.cvRequired;
  const isAtCoverLetterLimit = computed(
    () => (application.value?.previousCoverLetters?.length ?? 0) >= 20,
  );

  const nationalityOptions = ref<NationalityOption[]>([]);

  onMounted(async () => {
    nationalityOptions.value = await getNationalitiesForLocale(locale.value);
  });

  const selectedNationality = computed({
    get: () => nationalityOptions.value.find((option) => option.value === values.nationality?.id),
    set: (option: NationalityOption | undefined) => {
      values.nationality = option ? { id: option.value, name: option.label } : null;
    },
  });

  const { values, state, validate } = useForm({
    initialData: () => toFormData(application.value),
    schema: z
      .object({
        firstName: z.string().min(2, t('REQUIRED_INPUT')),
        lastName: z.string().min(2, t('REQUIRED_INPUT')),
        phone: z.object({
          countryCode: z.string(),
          number: z
            .string()
            .min(6, t('JOB_APPLICATION_PAGE.PHONE_NUMBER_MIN_LENGTH_ERROR', 6))
            .or(z.literal('')),
        }),
        email: z
          .string()
          .email(t('JOB_APPLICATION_PAGE.EMAIL_FORMAT_ERROR'))
          .optional()
          .or(z.literal('')),
        nationality: z
          .object({
            id: z.number(),
            name: z.string(),
          })
          .optional()
          .nullable(),
        updateMyProfile: z.boolean(),
        otherDocuments: z.string().array(),
        profileCvs: z.number().array(),
        cvs: z.string().array(),
        coverLetter: z.string().optional().nullable(),
        saveCoverLetter: z.boolean().default(false),
      })
      .superRefine(({ email, phone, profileCvs, cvs }, ctx) => {
        if (isEmptyString(email) && isEmptyString(phone?.number)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: t('JOB_APPLICATION_PAGE.EMAIL_OR_PHONE_REQUIRED_ERROR'),
            path: ['email', 'phone'],
          });
        }

        if (isCvRequired && profileCvs.length === 0 && cvs.length === 0) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: t('JOB_APPLICATION_PAGE.CV_REQUIRED_ERROR'),
            path: ['profileCvs', 'cvs'],
          });
        }
      }),
  });

  const accordions = reactive({
    coverLetter: !isNull(values.coverLetter) && !isEmptyString(values.coverLetter),
    otherDocuments: !isEmptyArray(values.otherDocuments ?? []),
  });

  function toFormData(application: JobApplicationFooBar | null) {
    const availableCvIds = application?.cvs ? application.cvs.map((d) => d.id) : [];
    const availableDocumentIds = application?.documents
      ? application.documents.map((d) => d.id)
      : [];
    const selectedCvs = application?.data.cvs
      ? application.data.cvs.filter((id) => availableCvIds.includes(id))
      : [];
    const selectedDocuments = application?.data.documents
      ? application.data.documents.filter((id) => availableDocumentIds.includes(id))
      : [];
    return {
      firstName: application?.data.firstName,
      lastName: application?.data.lastName,
      phone: {
        countryCode: application?.data.phone?.countryCode ?? '',
        number: application?.data.phone?.number ?? '',
      },
      email: application?.data.email,
      nationality: application?.data.nationality,
      updateMyProfile: false,
      profileCvs: application?.data.profileCvs || [],
      cvs: selectedCvs,
      otherDocuments: selectedDocuments,
      coverLetter: application?.data.coverLetter,
      saveCoverLetter: application?.data.saveCoverLetter ?? false,
    };
  }

  function scrollToTheNearestErrorField() {
    const errorElement = document.querySelector(`*[class*="--error"]`);
    errorElement?.scrollIntoView({ block: 'center', behavior: 'smooth' });
  }

  async function onSubmit() {
    if (await validate()) {
      saveFormData(values);
      emit('next');
      scrollToTop();
    } else {
      scrollToTheNearestErrorField();
      pushToastMessage({
        type: 'alert',
        title: t('JOB_APPLICATION_PAGE.INVALID_FORM_TOAST.TITLE'),
        subtitle: t('JOB_APPLICATION_PAGE.INVALID_FORM_TOAST.SUBTITLE'),
      });
    }
  }

  function openLeavePageWarningModal() {
    return openMpConfirmModal({
      title: t('JOB_APPLICATION_PAGE.LEAVE_PAGE_CONFIRM_MODAL.TITLE'),
      text: t('JOB_APPLICATION_PAGE.LEAVE_PAGE_CONFIRM_MODAL.TEXT'),
      confirmButtonLabel: t('JOB_APPLICATION_PAGE.LEAVE_PAGE_CONFIRM_MODAL.CONFIRM_BUTTON_LABEL'),
      cancelButtonLabel: t('JOB_APPLICATION_PAGE.LEAVE_PAGE_CONFIRM_MODAL.CANCEL_BUTTON_LABEL'),
    });
  }

  function onBeforeUnload(event: BeforeUnloadEvent) {
    return (event.returnValue = 'Are you sure you want to leave?');
  }

  onBeforeRouteLeave((to) => {
    const allowedRedirects = ['apply-expired', 'apply-unsupported-application-type'];
    const source = to.query.source?.toString() || '';

    if (!allowedRedirects.includes(source)) {
      return openLeavePageWarningModal();
    }
  });

  onMounted(() => {
    window.addEventListener('beforeunload', onBeforeUnload);
  });

  onUnmounted(() => {
    window.removeEventListener('beforeunload', onBeforeUnload);
  });

  function saveFormData(data: typeof values, debounced = false) {
    const applicationData = {
      firstName: data.firstName ?? '',
      lastName: data.lastName ?? '',
      phone: data.phone ?? {
        countryCode: '',
        number: '',
      },
      email: data.email ?? '',
      nationality: data.nationality,
      coverLetter: data.coverLetter ?? '',
      saveCoverLetter: data.saveCoverLetter ?? false,
      profileCvs: data.profileCvs,
      cvs: data.cvs,
      documents: data.otherDocuments,
    };
    debounced ? form.saveDebounced(applicationData) : form.save(applicationData);
  }

  watch(values, (data) => saveFormData(data, true));

  const selectedCoverLetterId = ref<string | null>(null);

  function handleCoverLetterSelect(letterId: string, letterContent: string) {
    try {
      if (!letterContent || typeof letterContent !== 'string') {
        throw new Error('Invalid cover letter content');
      }

      selectedCoverLetterId.value = letterId;
      values.coverLetter = letterContent;
    } catch (error) {
      console.error('Failed to apply previous cover letter:', error);
    }
  }

  async function handleCoverLetterRename(letterEntityId: number, newName: string) {
    if (!application.value?.id) {
      return;
    }

    try {
      await apiFetch(`/me/cover-letters/${letterEntityId}/title`, {
        method: 'PATCH',
        body: { newTitle: newName },
      });

      // Update the local application data
      if (application.value.previousCoverLetters) {
        const letterIndex = application.value.previousCoverLetters.findIndex(
          (letter) => letter.id === letterEntityId,
        );
        if (letterIndex !== -1) {
          application.value.previousCoverLetters[letterIndex].title = newName;
        }
      }
    } catch (error) {
      console.error('Failed to rename cover letter:', error);
    }
  }

  async function handleCoverLetterDelete(letterEntityId: number) {
    if (!application.value?.id) {
      return;
    }

    try {
      await apiFetch(`/me/cover-letters/${letterEntityId}`, {
        method: 'DELETE',
      });

      // Update the local application data
      if (application.value.previousCoverLetters) {
        const letterIndex = application.value.previousCoverLetters.findIndex(
          (letter) => letter.id === letterEntityId,
        );
        if (letterIndex !== -1) {
          application.value.previousCoverLetters.splice(letterIndex, 1);
        }
      }
    } catch (error) {
      console.error('Failed to delete cover letter:', error);
    }
  }
</script>

<template>
  <form
    id="application-form"
    class="application"
    @submit.prevent="onSubmit"
  >
    <MpCard
      border
      shadow
    >
      <MpText
        as="h5"
        uppercase
      >
        {{ $t('JOB_APPLICATION_PAGE.CONTACT_SECTION_TITLE') }}
      </MpText>
      <div
        v-if="!userStore.isAuthenticated"
        class="contact-description-info"
      >
        <MpIcon icon="information" />
        <div class="contact-description-info__text">
          <div>
            <i18n-t
              keypath="JOB_APPLICATION_PAGE.CONTACT_DESCRIPTION.APPLY"
              scope="global"
            >
              <template #login>
                <LinksLocale :to="{ name: 'auth-login', query: { redirect: route.fullPath } }">
                  <MpText
                    semibold
                    link
                  >
                    {{ $t('JOB_APPLICATION_PAGE.CONTACT_DESCRIPTION.LOGIN') }}
                  </MpText>
                </LinksLocale>
              </template>
            </i18n-t>
          </div>
          <MpText no-margin>
            {{ $t('JOB_APPLICATION_PAGE.CONTACT_DESCRIPTION.CONTINUE') }}
          </MpText>
        </div>
      </div>
      <div class="application__basic-info">
        <MpInput
          v-model="values.firstName"
          :label="$t('JOB_APPLICATION_PAGE.FIRST_NAME_LABEL')"
          data-test="job-application-first-name"
          name="fname"
          required
          :disabled="disabled"
          :error="state.firstName.message"
        />
        <MpInput
          v-model="values.lastName"
          :label="$t('JOB_APPLICATION_PAGE.LAST_NAME_LABEL')"
          data-test="job-application-last-name"
          name="lname"
          required
          :disabled="disabled"
          :error="state.lastName.message"
        />
        <SelectPhone
          v-model="values.phone"
          :number-label="$t('JOB_APPLICATION_PAGE.PHONE_NUMBER_LABEL')"
          :disabled="disabled"
          :error="state.phone.message"
        />
        <MpInput
          v-model="values.email"
          data-test="job-application-email"
          :label="$t('JOB_APPLICATION_PAGE.EMAIL_LABEL')"
          :disabled="disabled || userStore.isAuthenticated"
          :error="state.email.message"
        />
        <MpSelect
          v-model="selectedNationality"
          data-test="job-application-nationality"
          :label="$t('JOB_APPLICATION_PAGE.NATIONALITY_LABEL')"
          :options="nationalityOptions"
          :disabled="disabled"
          :max-menu-height="200"
          :error="state.nationality.message"
          placeholder=""
          searchable
          class="nationality-field"
        />
      </div>
      <MpDivider margin="lg" />
      <MpField :error="state.profileCvs.message || state.cvs.message || undefined">
        <MpText
          as="h5"
          no-margin
          uppercase
        >
          {{ $t('JOB_APPLICATION_PAGE.CVS_SECTION_TITLE') }}
        </MpText>
      </MpField>
      <MpText
        v-if="!isCvRequired"
        as="p"
        class="application__basic-info__cv-not-required"
      >
        {{ `(${$t('NOT_REQUIRED').toLowerCase()})` }}
      </MpText>
      <i18n-t
        v-if="!userStore.isAuthenticated"
        keypath="JOB_APPLICATION_PAGE.MAKE_BIOGRAPHY.TEXT"
        scope="global"
        tag="div"
        class="application__make-profile"
      >
        <template #make>
          <LinksLocale :to="{ name: 'auth-login', query: { redirect: route.fullPath } }">
            <MpText
              semibold
              link
            >
              {{ $t('JOB_APPLICATION_PAGE.MAKE_BIOGRAPHY.MAKE') }}
            </MpText>
          </LinksLocale>
        </template>
      </i18n-t>
      <CVsFolder
        v-model:profile-cvs="values.profileCvs"
        v-model:cvs="values.cvs"
        test="job-application-cv-upload"
        :job-id="jobId"
      />
      <template v-if="projectConfig.features.extendedApplicationForm">
        <MpDivider margin="lg" />
        <MpAccordion
          v-model="accordions.coverLetter"
          :title="$t('JOB_APPLICATION_PAGE.COVER_LETTER_SECTION_TITLE').toUpperCase()"
          :subtitle="`(${t('NOT_REQUIRED').toLowerCase()})`"
          no-border
          no-padding
          scroll-into-view
        >
          <div class="application__cover-letter">
            <MpTextArea
              v-model="values.coverLetter"
              :placeholder="$t('JOB_APPLICATION_PAGE.COVER_LETTER_PLACEHOLDER')"
              rows="10"
              :disabled="disabled"
            />

            <!-- Save Cover Letter Checkbox -->
            <div
              v-if="userStore.isAuthenticated"
              class="application__cover-letter-save"
            >
              <MpCheckbox
                v-model="values.saveCoverLetter"
                :disabled="disabled || isAtCoverLetterLimit"
                :label="t('JOB_APPLICATION_PAGE.SAVE_COVER_LETTER_CHECKBOX')"
              />
              <MpText
                v-if="isAtCoverLetterLimit"
                type="body2"
                no-margin
                class="application__cover-letter-limit-message"
              >
                {{ t('JOB_APPLICATION_PAGE.COVER_LETTER_LIMIT_MESSAGE') }}
              </MpText>
            </div>

            <!-- Previous Cover Letters List -->
            <div
              v-if="application?.previousCoverLetters?.length"
              class="previous-cover-letter-list"
            >
              <MpText
                type="h5"
                class="previous-cover-letter-list__title"
              >
                {{ $t('JOB_APPLICATION_PAGE.PREVIOUS_COVER_LETTERS_TITLE') }}
              </MpText>
              <PreviousCoverLettersList
                :letters="application.previousCoverLetters"
                :selected-letter="selectedCoverLetterId"
                @select="handleCoverLetterSelect"
                @rename="handleCoverLetterRename"
                @delete="handleCoverLetterDelete"
              />
            </div>
          </div>
        </MpAccordion>
      </template>
      <template v-if="projectConfig.features.extendedApplicationForm">
        <MpDivider margin="lg" />
        <MpField :error="state.otherDocuments.message || undefined"></MpField>
        <MpAccordion
          v-model="accordions.otherDocuments"
          :title="$t('JOB_APPLICATION_PAGE.OTHER_DOCUMENTS_SECTION_TITLE').toUpperCase()"
          :subtitle="`(${t('NOT_REQUIRED').toLowerCase()})`"
          no-border
          no-padding
          scroll-into-view
        >
          <OtherDocumentsFolder v-model="values.otherDocuments" />
        </MpAccordion>
      </template>
    </MpCard>

    <MpCard
      border
      shadow
      class="terms"
    >
      <MpIcon
        icon="check_circle"
        size="lg"
      />

      <div>
        <i18n-t
          keypath="JOB_APPLICATION_PAGE.TOS_PRIVACY_ACCEPTANCE_TEXT"
          scope="global"
        >
          <template #tos>
            <LinksLocale
              :to="{ name: 'info-slug', params: { slug: $t('BRAND_LINKS.TOS.SLUG') } }"
              target="_blank"
            >
              <MpText
                semibold
                link
              >
                {{ $t('JOB_APPLICATION_PAGE.TOS') }}
              </MpText>
            </LinksLocale>
          </template>
          <template #privacy>
            <LinksLocale
              :to="{ name: 'info-slug', params: { slug: $t('BRAND_LINKS.PRIVACY.SLUG') } }"
              target="_blank"
            >
              <MpText
                semibold
                link
              >
                {{ $t('JOB_APPLICATION_PAGE.PRIVACY') }}
              </MpText>
            </LinksLocale>
          </template>
        </i18n-t>
        <MpText no-margin>
          {{ ` ${projectConfig.title}.` }}
        </MpText>
      </div>
    </MpCard>

    <JobActionsBar>
      <MpButton
        type="submit"
        data-test="job-application-continue-button"
        :text="$t('JOB_APPLICATION_PAGE.CONTINUE_BUTTON_LABEL')"
        :is-full-width="isMobile"
        :disabled="disabled"
      />
    </JobActionsBar>
  </form>
</template>

<style scoped lang="scss">
  .application {
    display: flex;
    flex-direction: column;
    gap: var(--mp-space-60);
    padding-bottom: 2em;

    &__basic-info {
      display: grid;
      grid-template-columns: 1fr;
      gap: var(--mp-space-40);
      margin-top: var(--mp-space-60);
      margin-bottom: var(--mp-space-60);

      @include screen-md {
        grid-template-columns: 1fr 1fr;
      }

      .nationality-field {
        @include screen-md {
          grid-column: 2;
        }
      }

      &__cv-not-required {
        margin: var(--mp-space-20) 0;
      }
    }

    &__make-profile {
      margin: var(--mp-space-50) 0 var(--mp-space-20) 0;
    }

    &__update {
      margin-top: var(--mp-space-60);
    }

    &__cover-letter {
      padding-top: var(--mp-space-40);
    }

    &__cover-letter-save {
      display: flex;
      flex-direction: column;
      align-items: flex-end;
      margin-top: var(--mp-space-40);

      &:last-child {
        margin-bottom: 0;
      }
    }

    &__cover-letter-limit-message {
      margin-top: var(--mp-space-30);
      color: var(--mp-color-status-warning);
    }
  }

  .previous-cover-letter-list {
    margin-top: var(--mp-space-40);

    &__title {
      display: inline-block;
      margin-bottom: var(--mp-space-30);
    }
  }

  .terms {
    display: flex;
    align-items: center;
    gap: var(--mp-space-40);
  }

  .contact-description-info {
    display: flex;
    gap: var(--mp-space-50);

    &__text {
      display: flex;
      flex-direction: column;
      gap: var(--mp-space-40);
    }
  }
</style>
